import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next'; // Import the translation hook
import Breadcrumb from '@/components/Breadcrumb';
import DynamicTable, { SelectColumnFilter } from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import { EditButton } from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import DefaultLayout from '@/layout/DefaultLayout';
import {
  VacationsResponse,
  deleteVacation,
  getVacations,
} from '@/services/VacationService'; // Update the path to the actual service
import { VacationType } from '@/enums/VacationTypeEnum';

function Vacations() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<string>('');
  const navigate = useNavigate();

  const { t } = useTranslation(); // Use the default namespace

  const ActionButtonsCell = ({ value }: any) => {
    return (
      <div>
        {/* <DeleteButton
          onClick={() => {
            setSelectedRow(value);
            setIsModalVisible(true);
          }}
        >
          {t('Vacation.deleteButton')} 
        </DeleteButton> */}

        <EditButton
          type="button"
          onClick={() => {
            navigate(`${value}`);
          }}
        >
          {t('Vacation.editButton')} {/* Translate the edit button */}
        </EditButton>
      </div>
    );
  };

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteVacation.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('Vacation.nameColumn'), // Translate the name column header
        accessor: 'name',
        Filter: SelectColumnFilter,
      },

      {
        Header: t('Vacation.description'),
        Cell: ({ cell: { value, row } }: any) => {
          var type = row.original['type'];

          return (
            <span className="flex w-[100px] items-center justify-center text-center">
              {t(`VacationType.${VacationType[type].toString()}`)}
            </span>
          );
        },
      },
      {
        Header: t('Vacation.totalDaysColumn'), // Translate the total days column header
        accessor: 'totalDays',
        Cell: ({ cell: { value, row } }: any) => {
          var isNotLimited = row.original['isNotLimited'];

          return (
            <span className="flex w-[100px] items-center justify-center text-center">
              {isNotLimited ? '--' : value}
            </span>
          );
        },
      },
      // {
      //   Header: t('Vacation.yearColumn'), // Translate the year column header
      //   accessor: 'year',
      // },
      // Add more columns as needed
      {
        Header: t('Vacation.actionsColumn'), // Translate the actions column header
        accessor: 'id',
        Cell: ({ cell: { value } }: any) => <ActionButtonsCell value={value} />,
      },
    ],
    [t] // Add the t function as a dependency
  );

  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    data: vacations,
  } = useQuery<VacationsResponse | undefined>(
    ['vacations', pageIndex, pageSize, search],
    () =>
      getVacations({
        fromvalue: pageIndex,
        takevalue: pageSize,
        search: search,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
    }
  );

  const handleDeleteVacation = useMutation(
    async (id: string) => {
      await deleteVacation(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'vacations',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  // const pageCount = useMemo(() => {
  //   return Math.ceil((appointments?.count ?? 0) / pageSize);
  // }, [pageSize, appointments]);

  // const nextPage = () => setPageIndex(pageIndex + 1);
  // const previousPage = () => setPageIndex(pageIndex - 1);
  // const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  // const onPageSizeChange = (newPageSize: number) => {
  //   setPageIndex(0);
  //   setPageSize(newPageSize);
  // };

  const pageCount = useMemo(() => {
    return Math.ceil((vacations?.count ?? 0) / pageSize);
  }, [pageSize, vacations]);

  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  return (
    <DefaultLayout>
      <Breadcrumb pageName={t('Vacation.pageName')} />
      {/* Translate the breadcrumb */}
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={vacations?.count ?? 0}
            data={vacations?.vacations ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
            exportExcel={false}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </DefaultLayout>
  );
}

export default Vacations;
